import ValidateModule from "./ValidateModule";

import type { IEventChannel, ApiOpts } from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";

const $: JQueryStatic = global.$;
const es = global.es;

/**
 * @class IntegrationSalesforceModule
 */
export default class IntegrationSalesforceModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    private isSyncingByGeoMapId: boolean;

    // See also: com.espatial.es.model.integration.action.salesforce.SalesforceObjectType
    // We could return it from server but for convenience will copy here.
    SalesforceObjectType = [
        "Account",
        "Lead",
        "Opportunity",
        "Contact",
        "Unknown", // Should only be used for reports created before v9.12 so the primary SFO type was not recorded. These report types are to be phased out in 9.13
        "Custom"
    ];
    
    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // flags to prevent first-line-of-defence protection against accidentally kicking off concurrent sync tasks
        this.isSyncingByGeoMapId = false;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.beginImportReport = this.beginImportReport.bind(this);
        this.doReportSync = this.doReportSync.bind(this);
        this.beginImportSalesforceObjects = this.beginImportSalesforceObjects.bind(this);
        this.encodeQuery = this.encodeQuery.bind(this);
        this.beginModifyImportSalesforceObjects = this.beginModifyImportSalesforceObjects.bind(this);
        this.doSalesforceImport = this.doSalesforceImport.bind(this);
        this.createTerritoryFromSalesforceCustomObject = this.createTerritoryFromSalesforceCustomObject.bind(this);
        this.publishTerritoryFromSalesforceCustomObject = this.publishTerritoryFromSalesforceCustomObject.bind(this);
        this.syncObjects = this.syncObjects.bind(this);
        this.isValidSoql = this.isValidSoql.bind(this);
        this.listLayerMeta = this.listLayerMeta.bind(this);
        this.syncTerritoryFromSalesforceCustomObject = this.syncTerritoryFromSalesforceCustomObject.bind(this);
        this.getSalesforceLayerViews = this.getSalesforceLayerViews.bind(this);
        this.syncSalesforceDatasets = this.syncSalesforceDatasets.bind(this);
        this.syncDatasetByGeoMapId = this.syncDatasetByGeoMapId.bind(this);
        this.monitorTaskProgress = this.monitorTaskProgress.bind(this);
        this.getSignedCanvasRequestJson = this.getSignedCanvasRequestJson.bind(this);
        this.getSampleData = this.getSampleData.bind(this);
        this.getDefaultAddressFields = this.getDefaultAddressFields.bind(this);
        this.setDefaultAddressFields = this.setDefaultAddressFields.bind(this);
        this.getSalesforceAttributesMapping = this.getSalesforceAttributesMapping.bind(this);
        this.sendSalesForceDiagnostic = this.sendSalesForceDiagnostic.bind(this);
        this.publish = this.publish.bind(this);
        this.publishTerritoryToSalesforce = this.publishTerritoryToSalesforce.bind(this);
        this.getJobInfo = this.getJobInfo.bind(this);
        this.listJobIds = this.listJobIds.bind(this);
        this.abortPublishJob = this.abortPublishJob.bind(this);
        this.getPublishFailResults = this.getPublishFailResults.bind(this);
        this.getChangedFieldsSummary = this.getChangedFieldsSummary.bind(this);
    }

    /**
     * Import of a report from Salesforce: i.e. report is downloaded from salesforce.com and saved in user httpUpload dir and
     * DataParserMetaObject is returned for user to to review before performing dataset creation.
     * 
     * @param reportId 
     * @param reportName 
     * @param defaultPresetAddress 
     * @param opts 
     * 
     * @returns 
     */
    beginImportReport(reportId, reportName, defaultPresetAddress, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/begin_import_report/" + reportId + "?reportName=" + encodeURIComponent(reportName);
        if (defaultPresetAddress) {
            url += '&defaultPresetAddress=' + encodeURIComponent(defaultPresetAddress);
        }
        
        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': null
        });
    }

    /**
     * Sync report using importMeta. If there is an externalId on the layerMeta then an upsert will performed, otherwise an overwrite.
     */
    doReportSync(layerMetaId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/do_salesforce_sync_report?layerMetaId=" + layerMetaId;

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': null
        });
    }

    // ---------------------

    /**
     * Import of objects (Lead, Account, etc) from Salesforce.
     * @return SalesforceDataParserMeta is returned for user to to review before performing dataset creation.
     */
    beginImportSalesforceObjects(soql: string, defaultPresetAddress?: string, opts?: ApiOpts) {
        soql = this.encodeQuery(soql);

        let url = es.api.getURL(this.resourceName) + "/begin_import_salesforce_objects?soql=" + (soql);
        if (defaultPresetAddress) {
            url += '&defaultPresetAddress=' + encodeURIComponent(defaultPresetAddress);
        }

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': null
        });
    }

    /**
     * 
     * @param query 
     * @returns 
     */
    private encodeQuery(query: string) {
        query = encodeURIComponent(query).replace(/%20/g, "+");
        return query;
    }

    /**
     * Modify Salesforce layer by adding new attributes. Import of objects (Lead, Account, etc) from Salesforce.
     * @return SalesforceDataParserMeta is returned for user to to review before performing dataset creation.
     */
    beginModifyImportSalesforceObjects(soql: string, defaultPresetAddress: string, layerMetaId: number, geoMapId: number, layerViewId?: number, opts?: ApiOpts) {
        soql = this.encodeQuery(soql);

        let url = es.api.getURL(this.resourceName) + "/begin_modify_import_salesforce_objects?soql=" + (soql);
        url += '&layerMetaId=' + layerMetaId;
        url += '&geoMapId=' + geoMapId;
        if (defaultPresetAddress) {
            url += '&defaultPresetAddress=' + encodeURIComponent(defaultPresetAddress);
        }
        if (layerViewId) {
            url += '&layerViewId=' + layerViewId;
        }

        return this.rqBroker.doPut({
            'url': url,
            'opts': opts,
            'data': null
        }).done(() => {
            this.cache.clear("LayerMeta");
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
            this.cache.clear("GeoMap");
        });
    }

    /**
     * 
     * @param importParams 
     * @param defaultPresetAddress 
     * @param isAllOrNothing 
     * @param opts 
     * 
     * @returns 
     */
    doSalesforceImport(importParams, defaultPresetAddress?: string, isAllOrNothing?: boolean, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/do_salesforce_import_objects?" +
            "&isAllOrNothing=" + (isAllOrNothing ? true : false) +
            ( defaultPresetAddress ? "&defaultPresetAddress=" + es.util.url.stringify(defaultPresetAddress) : "");

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: importParams
        }).done(() => {
            this.cache.clear("LayerMeta");
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
        });

    }

    /**
     * 
     * @param territorySFCustomObjectWrapper 
     * @param opts 
     * 
     * @returns 
     */
    createTerritoryFromSalesforceCustomObject(territorySFCustomObjectWrapper, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/sf_ter_custom_obj";

        return this.rqBroker.doPost({
            url: url,
            opts: opts,
            data: territorySFCustomObjectWrapper
        });
    }

    /**
     * 
     * @param territoryGeoMapId 
     * @param opts 
     * 
     * @returns 
     */
    publishTerritoryFromSalesforceCustomObject(territoryGeoMapId: number, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/sf_publish_ter_custom_obj/" + territoryGeoMapId;

        return this.rqBroker.doPost({
            url: url,
            opts: opts
        });
    }
    
    /**
     * Sync data from Salesforce using importMeta of given layerViewId.
     * 
     * @param layerViewId 
     * @param layerMetaId 
     * @param doOverwrite 
     * @param opts 
     * 
     * @returns 
     */
    syncObjects(layerViewId: number|null, layerMetaId: number|null, doOverwrite?: boolean, opts?: ApiOpts) {
        //es.log.info("AUTO-SYNCING", layerViewId, layerMetaId);
        const url = es.api.getURL(this.resourceName) + "/salesforce_sync_objects?" +
                (layerViewId ? "layerViewId=" + layerViewId : "") +
                (layerMetaId ? "layerMetaId=" + layerMetaId : "") +
                "&doOverwrite=" + (doOverwrite == true ? "true" : "false");

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': null
        }).done(() => {
            this.cache.clear("LayerMeta");
            this.cache.clear("LayerInfo");
            this.cache.clear("LayerView");
        });
    }

    /**
     * Check SOQL is valid syntax by parsing with https://github.com/mulesoft/salesforce-soql-parser
     * 
     * @param soql 
     * @param opts 
     * 
     * @returns 
     */
    isValidSoql(soql: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/is_valid_soql";

        return this.rqBroker.doPost({
            'url': url,
            'opts': opts,
            'data': soql
        });
    }

    /**
     * 
     * @param objectType 
     * @param customObjectType 
     * @param opts 
     * 
     * @returns 
     */
    listLayerMeta(objectType:string, customObjectType: string, opts?: ApiOpts) {
        let url = es.api.getURL(this.resourceName) + "/list_layer_meta/" + objectType;
        if (customObjectType) {
            url += '?customObjectType=' + encodeURIComponent(customObjectType);
        }

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts,
            'data': null
        }).done((layerMetas) => {
            layerMetas.forEach((lm) => this.cache.set("LayerMeta", lm.id, lm));
        });
    }

    /**
     * 
     * @param geoMapId 
     * 
     * @returns 
     */
    private syncTerritoryFromSalesforceCustomObject(geoMapId: number) {
        return es.api.geoMap.get(geoMapId).then((geoMap) => {
            return (geoMap.$type == "TerritoryTempGeoMap" // TODO FIXME can't we sync on master copy?!
                    ? es.api.territory.getTerritoryMetaByGeoMapId(geoMapId)
                    : es.util.deferred.rejected('Must sync on TerritoryTempGeoMap')); // should show error msg?
        }).then((territoryMeta) => {
            if (!territoryMeta?.sfProps?.customObject) {
                return es.util.deferred.rejected('Not SF custom object AB territory');
            }

            const url = es.api.getURL(this.resourceName) + "/sf_sync_ter_custom_obj/" + geoMapId;
            return this.rqBroker.doPost({url: url});
        });
    }

    /**
     * 
     * @param geoMapId 
     * 
     * @returns 
     */
    private getSalesforceLayerViews(geoMapId: number) {
        return es.api.layerView.getByGeoMapExcludeAnalysis(geoMapId).then((layerViews) => {
            layerViews = layerViews
                .filter(lv => lv.importMeta)
                .filter(lv => es.util.salesforce.isSfImportMeta(lv.importMeta));
            
            return layerViews;
        });
    }

    /**
     * 
     * @param layerViews 
     * @param isFullSync 
     * 
     * @returns 
     */
    private async syncSalesforceDatasets(layerViews: object[], isFullSync: boolean) {
        const startSyncPromises = layerViews.map(lv => {
            const importMeta = lv.importMeta;

            if (importMeta.$type === "SalesforceReportImportMeta") {
                return this.doReportSync(lv.layerMetaId);
            }
            
            if (importMeta.$type === "SalesforceObjectImportMeta") {
                return this.syncObjects(lv.id, null, isFullSync);
            }

            throw new Error("FULL Sync for " + (importMeta.$type ? importMeta.$type : "datasets ") + " not implemented");
        });

        return Promise.all(startSyncPromises);
    }

    /**
     * This method cannot be run concurrently.
     * 
     * @param geoMapId 
     * @param isFullSync 
     * 
     * @returns 
     */
    public syncDatasetByGeoMapId(geoMapId: number, isFullSync: boolean) {
        if (this.isSyncingByGeoMapId) {
            throw new Error("syncDatasetByGeoMapId method cannot be run concurrently.");
        }

        this.isSyncingByGeoMapId = true;

        const isSyncTerritoryFromSalesforceCustomObjectNeeded = (geoMapId: number) => {
            return es.api.geoMap.get(geoMapId).then((geoMap) => {
                if (geoMap.$type === "TerritoryTempGeoMap") { // TODO FIXME can't we sync on master copy?!
                    return es.api.territory.getTerritoryMetaByGeoMapId(geoMapId).then(territoryMeta => {
                        return !!(territoryMeta?.sfProps);
                    });
                }
                return es.util.deferred.resolved(false);
            });
        };

        return this.getSalesforceLayerViews(geoMapId).then((layerViews) => {
            return Promise.all([
                es.util.deferred.resolved(layerViews),
                this.syncSalesforceDatasets(layerViews, isFullSync),
                isSyncTerritoryFromSalesforceCustomObjectNeeded(geoMapId).then((isNeeded) => {
                    if (isNeeded) {
                        return this.syncTerritoryFromSalesforceCustomObject(geoMapId);
                    }

                    return es.util.deferred.resolved(false);
                })
            ]);
        }).then(([layerViews, sfDatasetsStatus, sfTerritoryStatus]) => {
            if (sfDatasetsStatus && !Array.isArray(sfDatasetsStatus)) { // if there's only 1 dataset, it will be resolved without being array
                sfDatasetsStatus = [sfDatasetsStatus];
            }

            let taskObjs = sfDatasetsStatus || [];
            if (sfTerritoryStatus) {
                taskObjs = taskObjs.concat(sfTerritoryStatus);
            }

            return es.util.deferred.resolved(layerViews, taskObjs);
        }).fail(() => {
            this.isSyncingByGeoMapId = false;
        }).then((layerViews, taskObjs) => {
            this.eventChannel?.fireEvent("onSyncDatasetStart", taskObjs);

            const clearCacheReloadLayerInfo = () => {
                this.cache.clear("LayerMeta");
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerView");
                this.cache.clear("Territory_loadAll");

                return Promise.all([
                    es.api.analysis.getByGeoMapId(geoMapId),
                    Promise.all(layerViews.map(lv => es.api.layerInfo.getForLayerView(lv.id)))
                ]).then(([analysis]) => {
                    if (!analysis) {
                        return es.util.deferred.resolved();
                    }

                    const lvIds = es.api.analysis.getGeneratedLayerViewIds(analysis);
                    const dfds = lvIds.map(lvid => es.api.layerInfo.getForLayerView(lvid));
                    return Promise.all(dfds).then(() => es.util.deferred.resolved());
                });
            };

            this.monitorTaskProgress(taskObjs).done(async (completedTask) => {
                //es.log.log("monitorTaskProgress", completedTask);
                try {
                    await clearCacheReloadLayerInfo();
                } finally {
                    this.eventChannel?.fireEvent("onSyncDatasetFinish", completedTask);
                }
            }).fail((failedTasks) => {
                clearCacheReloadLayerInfo();

                for (let i = 0; i < failedTasks.length; i++) {
                    if (failedTasks[i].statusObj) {
                        es.error.handleJSError(failedTasks[i].statusObj.errorMessage);
                    } else {
                        es.error.handleJSError(failedTasks[i].errorMessage);
                    }
                }
            }).always(() => {
                this.isSyncingByGeoMapId = false;
            });
            return taskObjs;
        });
    }

    /**
     * internal used method to track imports
     * 
     * @param taskObjs 
     * 
     * @returns 
     */
    private monitorTaskProgress(taskObjs) {
        const endSyncDfds = [];
        for(let i = 0; i < taskObjs.length; i++){
            endSyncDfds.push(es.api.Import.poll(taskObjs[i].taskId));
        }

        return $.when.apply($, endSyncDfds).then(function() { // callback needs to be in function to capture arguments
            const failStatuses = [];
            for(let i = 0; i < arguments.length; i++){
                if(arguments[i].statusObj["errorMessage"]){
                    failStatuses.push(arguments[i].statusObj);
                } else if(arguments[i]["errorMessage"]){
                    failStatuses.push(arguments[i]["errorMessage"]);
                }
            }
            
            if (failStatuses.length > 0) {
                return es.util.deferred.rejected(failStatuses);
            }
            
            return Array.from(arguments);
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    getSignedCanvasRequestJson(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/signed_request";

        return this.rqBroker.doPost({url: url, opts: opts});
    }

    /**
     * Fetch the server-stored PKCE access token for the current eSpatial user + given SF user.
     * The server refreshes via the stored refresh_token if the access_token is within 5 min of
     * expiry. Returns 404 if no token is stored (caller should bounce through the popup re-auth).
     *
     * The access_token is returned as a string - callers should treat it as a bearer credential
     * (no logging, no storage outside in-memory client state).
     */
    pkceToken(sfUserId: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/pkce_token?sfUserId=" + encodeURIComponent(sfUserId || "");
        return this.rqBroker.doGet({url: url, opts: opts, data: null});
    }
    
    /**
     * 
     * @param soql 
     * @param opts 
     * 
     * @returns 
     */
    getSampleData(soql: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(soql)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/sample_data?soql=' + encodeURIComponent(soql);
        return this.rqBroker.doGet({url: url, opts: opts});
    }
    
    /**
     * 
     * @param objectType 
     * @param opts 
     * 
     * @returns 
     */
    getDefaultAddressFields(objectType: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(objectType)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/default_address_fields/' + encodeURIComponent(objectType);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, objectType, opts);
    }

    /**
     * 
     * @param objectType 
     * @param addressFields 
     * @param region 
     * @param opts 
     * 
     * @returns 
     */
    setDefaultAddressFields(objectType: string, addressFields: string[], region?: string, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(objectType)) {
            return es.util.deferred.rejected();
        }
        
        if (!ValidateModule.checkMandatoryArray(addressFields, true)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/default_address_fields/' + encodeURIComponent(objectType);
        const data = {
            'addressFields': addressFields,
            'region': region
        };
        
        return this.rqBroker.doPut({
            'url': url,
            'data': data,
            'opts': opts
        }).done((rs) => {
            this.cache.set(this.resourceName, objectType, rs);
        });
    }
    
    /**
     * 
     * @param layerMetaId 
     * @param opts 
     * 
     * @returns 
     */
    getSalesforceAttributesMapping(layerMetaId: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(layerMetaId)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + '/sf_attributes_mapping?layerMetaId=' + layerMetaId;

        return this.rqBroker.doGet({
            'url': url,
            'opts': opts,
            'data': null
        });
    }

    /**
     * 
     * @param data 
     * @param opts 
     * 
     * @returns 
     */
    sendSalesForceDiagnostic(data: object, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName + '/sf_diagnostic');

        return this.rqBroker.doPost({
            url: url,
            data: data,
            opts: opts
        });
    }

    /**
     * 
     * @param layerViewId 
     * @param isUpdateAuxiliaryFields 
     * @param opts 
     * 
     * @returns 
     */
    private publish(layerViewId: number, isUpdateAuxiliaryFields?: boolean, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/publish/" + layerViewId + "?isUpdateAuxiliaryFields=" + (!!isUpdateAuxiliaryFields);

        return this.rqBroker.doPost({
            url: url,
            data: null,
            opts: opts
        });
    }

    /**
     * Publish territory layers to SFDC platform.
     *
     * Note: this method cannot be run concurrently.
     *
     * @param lvSettings [{lvId: 1, isUpdateAuxFields: true}, {lvId: 2, isUpdateAuxFields: false}]
     * 
     * @returns
     */
    async publishTerritoryToSalesforce(lvSettings: Array<{lvId: number, isUpdateAuxFields: boolean}>): Promise<any[]> {
        const startSyncPromises = lvSettings.map(setting =>
            this.publish(setting.lvId, setting.isUpdateAuxFields)
        );

        try {
            return await Promise.all(startSyncPromises);
        } catch (error) {
            console.error("publishTerritoryToSalesforce fail", error);
            throw error;
        }
    }

    /**
     * 
     * @param jobId 
     * @param opts 
     * 
     * @returns 
     */
    getJobInfo(jobId: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/publish_status/' + jobId;

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param opts 
     * 
     * @returns 
     */
    listJobIds(opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/publish_list_job_ids';

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param jobId 
     * @param opts 
     * 
     * @returns 
     */
    abortPublishJob(jobId: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/publish/' + jobId;

        return this.rqBroker.doDelete({
            url: url,
            opts: opts
        });
    }

    /**
     * 
     * @param jobId 
     * @param opts 
     * 
     * @returns 
     */
    getPublishFailResults(jobId: string, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/publish_fail_results/' + jobId;

        return $.fileDownload(url, opts);
    }

    /**
     * 
     * @param geoMapId 
     * @param opts 
     * 
     * @returns 
     */
    getChangedFieldsSummary(geoMapId: number, opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + '/changed_fields_summary?geoMapId=' + geoMapId;

        return this.rqBroker.doGet({
            url: url,
            opts: opts
        });
    }
}
